import React from "react";
import { Lightning, Plug, Wrench, BatteryFull, Leaf, CurrencyInr, ArrowRight, Check, Truck, Users, Gauge, Sparkle } from "@phosphor-icons/react";
import { COMPANY, IMAGES } from "@/data/site";
import LeadForm from "@/components/LeadForm";

const MODELS = [
  {
    t: "CKD — Single Light Active",
    tag: "City Commuter",
    d: "Perfect for everyday city commuting.",
    variants: [
      { batt: "48V / 26Ah Li-ion", range: "40–50 km", price: "₹57,000" },
      { batt: "60V / 30Ah Li-ion", range: "60–70 km", price: "₹67,000" },
      { batt: "60V / 45Ah Li-ion", range: "70–80 km", price: "₹74,000" },
      { batt: "60V / 60Ah Li-ion", range: "90–110 km", price: "₹98,000" },
    ],
  },
  {
    t: "CKD — Double Light Active",
    tag: "Dual Headlight",
    d: "Enhanced visibility with premium dual-headlight styling.",
    variants: [
      { batt: "48V / 26Ah Li-ion", range: "40–50 km", price: "₹59,000" },
      { batt: "60V / 30Ah Li-ion", range: "60–70 km", price: "₹69,000" },
      { batt: "60V / 45Ah Li-ion", range: "70–80 km", price: "₹76,000" },
      { batt: "60V / 60Ah Li-ion", range: "90–110 km", price: "₹99,000" },
    ],
  },
  {
    t: "CKD — Active FH",
    tag: "Premium Range",
    featured: true,
    d: "Premium performance with extended range and superior comfort.",
    variants: [
      { batt: "48V / 26Ah Li-ion", range: "40–50 km", price: "₹69,000" },
      { batt: "60V / 30Ah Li-ion", range: "60–70 km", price: "₹79,000" },
      { batt: "60V / 45Ah Li-ion", range: "70–80 km", price: "₹86,000" },
      { batt: "60V / 60Ah Li-ion", range: "90–110 km", price: "₹1,09,000" },
    ],
  },
];

const COMMERCIAL = [
  "Electric Cargo Loaders",
  "Passenger Electric Loaders",
  "Electric Three-Wheelers",
  "Commercial EV Fleet Solutions",
  "Custom Battery Configurations",
  "Business & Institutional Bulk Orders",
];

const INCLUDED = [
  "Genuine Lithium-ion Batteries",
  "Smart Charger Included",
  "Digital Display",
  "LED Lighting",
  "High-Torque BLDC Motor",
  "Low Maintenance",
  "Spare Parts Availability",
  "Sales & Service Support",
  "Warranty Support",
  "EMI & Finance Assistance Available",
];

const SERVICES = [
  { icon: Wrench, t: "Sales", d: "Hand-picked models for Tamil Nadu roads" },
  { icon: Wrench, t: "Service", d: "Doorstep service + factory-trained technicians" },
  { icon: BatteryFull, t: "Battery Upgrade", d: "Lithium retrofits & swaps" },
  { icon: Plug, t: "Spare Parts", d: "OEM parts, fast replacement" },
];

const BENEFITS = [
  { icon: CurrencyInr, t: "₹0.30/km", d: "Vs ₹2.50/km petrol" },
  { icon: Leaf, t: "Zero Emission", d: "Tamil Nadu cleaner with every km" },
  { icon: Wrench, t: "Low Maintenance", d: "No oil change, no clutch" },
  { icon: CurrencyInr, t: "FAME-II Subsidy", d: "Up to ₹15,000 incentive" },
];

export default function EV() {
  return (
    <div data-testid="ev-page">
      <section className="relative bg-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.ev} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <div className="label-eyebrow text-emerald-600">EV Mobility Division</div>
          <h1 className="mt-4 font-heading font-black text-5xl md:text-7xl tracking-tighter text-slate-950 max-w-3xl">
            Drive the future. <span className="italic font-light">Today.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-slate-700">Premium electric scooters with home delivery, free charger installation &amp; 3-year warranty.</p>
        </div>
      </section>

      <section className="bg-slate-50 py-20" data-testid="ev-models-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">Electric Scooters</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-950 max-w-3xl">3 Models. Every Budget. <span className="italic font-light">Built for every journey.</span></h2>
          <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">Experience reliable, eco-friendly mobility with our premium range of CKD electric scooters. From affordable daily commuters to long-range models — the perfect fit for you.</p>

          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            {MODELS.map((m, i) => (
              <div key={i} className={`card-flat overflow-hidden flex flex-col ${m.featured ? "ring-2 ring-emerald-500" : ""}`} data-testid={`ev-model-${i}`}>
                <div className={`p-7 ${m.featured ? "bg-emerald-500 text-white" : "bg-slate-950 text-white"}`}>
                  <div className="flex items-center justify-between">
                    <Lightning size={26} weight="fill" className={m.featured ? "text-white" : "text-emerald-400"} />
                    <span className={`text-[10px] font-bold uppercase tracking-[0.18em] ${m.featured ? "text-emerald-50" : "text-slate-400"}`}>{m.featured ? "★ " : ""}{m.tag}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-2xl font-bold leading-tight">{m.t}</h3>
                  <p className={`mt-2 text-sm ${m.featured ? "text-emerald-50" : "text-slate-300"}`}>{m.d}</p>
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-12 px-6 py-3 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <div className="col-span-6">Battery</div>
                    <div className="col-span-3">Range*</div>
                    <div className="col-span-3 text-right">Price</div>
                  </div>
                  {m.variants.map((v, k) => (
                    <div key={k} className="grid grid-cols-12 px-6 py-3.5 border-b border-slate-100 items-center" data-testid={`ev-model-${i}-variant-${k}`}>
                      <div className="col-span-6 text-xs font-medium text-slate-700">{v.batt}</div>
                      <div className="col-span-3 text-xs text-slate-500">{v.range}</div>
                      <div className="col-span-3 text-right font-heading font-bold text-sm text-slate-950">{v.price}</div>
                    </div>
                  ))}
                </div>
                <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent("Hi, I'm interested in the " + m.t + " electric scooter.")}`} target="_blank" rel="noopener noreferrer" className={`block text-center py-4 text-sm font-semibold transition-colors ${m.featured ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-950 text-white hover:bg-slate-800"}`} data-testid={`ev-model-${i}-enquire`}>
                  Enquire / Book Test Ride →
                </a>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500">*Actual riding range may vary depending on rider weight, road conditions, riding style, battery health and weather.</p>
        </div>
      </section>

      {/* COMMERCIAL EV */}
      <section className="bg-slate-950 text-white py-20" data-testid="ev-commercial-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="label-eyebrow text-emerald-400">Commercial Electric Vehicles</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter">Electric mobility for business &amp; industry.</h2>
            <p className="mt-5 text-slate-300 leading-relaxed">Beyond scooters — we supply electric mobility solutions for commercial and industrial applications. Available on requirement.</p>
            <p className="mt-4 text-sm text-slate-400">Contact us for pricing, specifications, finance options and dealership enquiries.</p>
            <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 bg-emerald-500 text-white px-6 py-3.5 text-sm font-semibold hover:bg-emerald-600" data-testid="ev-commercial-cta">
              Enquire Commercial EVs <ArrowRight size={14} weight="bold" />
            </a>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800 self-start">
            {COMMERCIAL.map((c, i) => {
              const icons = [Truck, Users, Gauge, Truck, BatteryFull, Sparkle];
              const Icon = icons[i] || Truck;
              return (
                <div key={i} className="bg-slate-950 p-6 flex items-start gap-3" data-testid={`ev-commercial-${i}`}>
                  <Icon size={22} weight="duotone" className="text-emerald-400 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-200">{c}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="bg-white py-20" data-testid="ev-included-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">What's Included</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Every scooter comes fully loaded.</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {INCLUDED.map((x, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-100" data-testid={`ev-included-${i}`}>
                <span className="w-6 h-6 bg-emerald-500 flex items-center justify-center shrink-0"><Check size={14} weight="bold" className="text-white" /></span>
                <span className="text-sm font-medium text-slate-800">{x}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" data-testid="ev-services-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">EV Services</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Everything for your EV.</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {SERVICES.map((s, i) => (
              <div key={i} className="bg-white p-7" data-testid={`ev-service-${i}`}>
                <s.icon size={28} weight="duotone" className="text-emerald-500" />
                <h4 className="mt-5 font-heading font-bold text-lg text-slate-950">{s.t}</h4>
                <p className="mt-2 text-sm text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-emerald-500 py-20" data-testid="ev-charging-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <div className="label-eyebrow !text-white/80">EV Charging</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter">Home &amp; commercial charging stations.</h2>
            <p className="mt-5 leading-relaxed">From 3.3kW home wall-mounts to 22kW AC and 60kW DC fast chargers. We design, install, license &amp; maintain.</p>
          </div>
          <div className="grid grid-cols-2 gap-px bg-emerald-700">
            <div className="bg-emerald-500 p-6 border border-white/20"><Plug size={32} weight="duotone" className="text-white" /><div className="mt-4 font-heading font-bold text-xl text-white">Home Charging</div><div className="text-sm text-white/90 mt-1">3.3 - 7.4 kW wall units</div></div>
            <div className="bg-emerald-500 p-6 border border-white/20"><Plug size={32} weight="duotone" className="text-white" /><div className="mt-4 font-heading font-bold text-xl text-white">Commercial</div><div className="text-sm text-white/90 mt-1">22-60 kW AC/DC stations</div></div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="ev-benefits-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">Benefits</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Why go electric.</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white p-7" data-testid={`ev-benefit-${i}`}>
                <b.icon size={32} weight="duotone" className="text-emerald-500" />
                <div className="mt-5 font-heading font-black text-2xl text-slate-950">{b.t}</div>
                <div className="mt-1 text-sm text-slate-600">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-emerald-500 py-16" data-testid="ev-final-cta">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="text-white">
            <h2 className="font-heading font-black text-3xl lg:text-5xl tracking-tighter">Ready to Go Electric?</h2>
            <p className="mt-3 text-emerald-50 font-medium">Book a Test Ride • Get a Free Quote • Finance Available</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent("Hi, I'd like to book a test ride.")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-slate-950 px-6 py-4 text-sm font-semibold hover:bg-slate-100" data-testid="ev-cta-testride">
              <Lightning size={16} weight="fill" /> Book a Test Ride
            </a>
            <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-4 text-sm font-semibold hover:bg-slate-800" data-testid="ev-cta-quote">
              Get a Free Quote <ArrowRight size={14} weight="bold" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8"><LeadForm source="ev" /></div>
      </section>
    </div>
  );
}
